/**
 * The end-to-end shielded-ZEC send orchestrator.
 *
 * Composition (called from the React Send screen):
 *
 *   1. forceFreshSync(ufvk)
 *        — refresh anchor + witnesses so they all agree.
 *   2. ensureCanonicalParams(saplingSpend/Output)
 *        — make sure proving keys are in IDB. Worker-decompressed if
 *          they only exist as compressed shards on-device.
 *   3. selectCoins(notes, amount, num_outputs, ...)
 *        — vendored from leokit-sdk; same ZIP-317 fee math.
 *   4. sign-worker → build_sapling_tx_v5
 *        — Groth16 proofs run in the worker, ~3-8 s on a phone.
 *   5. ShieldedClient.txBroadcast(tx_hex)
 *        — relays to lightwalletd via the shielded backend.
 *   6. markSpentLocally(spent_nullifiers)
 *        — optimistic prune so the next send doesn't see those notes.
 *
 * Returns the final txid + fee. Throws on any of:
 *   - INSUFFICIENT_FUNDS (from coin-select)
 *   - PARAMS_FETCH_FAILED (from ensure)
 *   - PROOF_FAILED (from the signer worker)
 *   - BROADCAST_FAILED (from the SDK)
 */

import { ensureCanonicalParams } from "../params";
import { getShieldedClient } from "../client";
import { forceFreshSync, markSpentLocally, type SpendableNote } from "../balance";
import { selectCoins, type CoinSelectError } from "./coin-select";
import { signInWorker, type BuiltTx } from "./sign-worker-client";

/** What the React Send screen passes in. */
export interface SendShieldedArgs {
  ufvk: string;
  seed_phrase: string;
  account?: number;
  /** u1… / zs1… / t1… / t3… — autodetected by the signer. */
  recipient: string;
  amount_zats: number;
  /** Free-text memo for the principal output. Sapling memos are 512 bytes;
   *  the signer truncates/encodes per ZIP-302. */
  memo?: string;
  /** MAYA-style shielded-memo swap: append a 0-value Sapling output
   *  encoding the swap memo to the protocol's UA. Principal stays as
   *  `recipient` (a t-address for MAYA). */
  memo_note?: {
    recipient_ua: string;
    memo_text: string;
  };
  /** Shielded backend root. Defaults to the configured URL — only
   *  override in tests. */
  shielded_server_url?: string;
}

export interface SendShieldedResult {
  txid: string;
  fee_zats: number;
  spent_nullifiers: string[];
}

export type SendShieldedErrorCode =
  | "NO_NOTES"
  | "INSUFFICIENT_FUNDS"
  | "ZERO_AMOUNT"
  | "PARAMS_FETCH_FAILED"
  | "PARAMS_TIMEOUT"
  | "PROOF_FAILED"
  | "BROADCAST_FAILED";

export class SendShieldedError extends Error {
  public readonly code: SendShieldedErrorCode;
  public readonly underlying?: unknown;
  constructor(code: SendShieldedErrorCode, message: string, underlying?: unknown) {
    super(message);
    this.name = "SendShieldedError";
    this.code = code;
    this.underlying = underlying;
  }
}

export interface SendProgress {
  stage:
    | "syncing-witnesses"
    | "loading-params"
    | "selecting-coins"
    | "proving"
    | "broadcasting"
    | "done";
  /**
   * Sub-progress for the long-running "loading-params" stage. Fires
   * only while ensureCanonicalParams is working. `pct` is 0-100 across
   * both shards (fetch + decompress).
   */
  params?: {
    pct: number;
    label: string;
  };
}

export async function sendShielded(
  args: SendShieldedArgs,
  onProgress?: (p: SendProgress) => void,
): Promise<SendShieldedResult> {
  // 1. Refresh notes + anchor + witnesses so they all agree.
  onProgress?.({ stage: "syncing-witnesses" });
  const fresh = await forceFreshSync(args.ufvk);
  if (fresh.notes.length === 0 || !fresh.anchor) {
    throw new SendShieldedError(
      "NO_NOTES",
      "no spendable shielded notes — wait for the scanner to finish syncing",
    );
  }

  // 2. Coin select before paying the params load cost — fail fast on
  //    insufficient funds.
  onProgress?.({ stage: "selecting-coins" });
  const recipientIsTransparent =
    args.recipient.startsWith("t1") || args.recipient.startsWith("t3");
  let picked: ReturnType<typeof selectCoins>;
  try {
    picked = selectCoins({
      notes: fresh.notes,
      amount_zats: args.amount_zats,
      num_outputs: 1,
      num_transparent_outputs: recipientIsTransparent ? 1 : 0,
      has_memo_note: !!args.memo_note,
    });
  } catch (e) {
    const ce = e as CoinSelectError;
    throw new SendShieldedError(ce.code as SendShieldedErrorCode, ce.message, e);
  }

  // 3. Make sure proving keys are in IndexedDB. First send pays the
  //    ~3-25 s decompress cost; subsequent sends skip straight through.
  //    We allocate a unified 0-100% bar across both shards:
  //      spend  fetch       → 0-30%
  //      spend  decompress  → 30-50%
  //      output fetch       → 50-80%
  //      output decompress  → 80-100%
  //    Spend params are ~10x heavier; fetch dominates the time on slow
  //    connections, decompress dominates on slow CPUs.
  onProgress?.({
    stage: "loading-params",
    params: { pct: 0, label: "Preparing proving keys…" },
  });
  const reportParams = (kind: "saplingSpend" | "saplingOutput") =>
    (p: import("../params").EnsureProgress) => {
      if (p.stage === "done") return;
      let pct = 0;
      let label = "Preparing proving keys…";
      if (kind === "saplingSpend") {
        if (p.stage === "fetching") {
          const r = p.total ? p.loaded / p.total : 0;
          pct = Math.round(r * 30);
          const mb = (p.loaded / 1024 / 1024).toFixed(1);
          const tot = p.total ? (p.total / 1024 / 1024).toFixed(1) : "?";
          label = `Downloading spend params · ${mb} / ${tot} MB`;
        } else if (p.stage === "decompressing") {
          pct = 32;
          label = "Decompressing spend params on device…";
        } else if (p.stage === "verifying" || p.stage === "writing") {
          pct = 48;
          label = "Verifying spend params…";
        } else if (p.stage === "checking") {
          pct = 0;
          label = "Checking local cache…";
        }
      } else {
        if (p.stage === "fetching") {
          const r = p.total ? p.loaded / p.total : 0;
          pct = 50 + Math.round(r * 30);
          const mb = (p.loaded / 1024 / 1024).toFixed(1);
          const tot = p.total ? (p.total / 1024 / 1024).toFixed(1) : "?";
          label = `Downloading output params · ${mb} / ${tot} MB`;
        } else if (p.stage === "decompressing") {
          pct = 82;
          label = "Decompressing output params on device…";
        } else if (p.stage === "verifying" || p.stage === "writing") {
          pct = 95;
          label = "Verifying output params…";
        } else if (p.stage === "checking") {
          pct = 50;
          label = "Checking output params cache…";
        }
      }
      onProgress?.({ stage: "loading-params", params: { pct, label } });
    };
  // Hard ceiling per shard. Even a slow phone with a 3G connection
  // should land both shards well under 3 minutes; longer than that and
  // the decompress worker has almost certainly hung (iOS Safari sometimes
  // suspends workers on backgrounded WebViews). Surface a clear error
  // so the user can retry instead of staring at a frozen progress bar.
  const SHARD_TIMEOUT_MS = 180_000;
  const withTimeout = <T>(p: Promise<T>, label: string): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const t = setTimeout(() => {
        reject(
          new SendShieldedError(
            "PARAMS_TIMEOUT",
            `${label} took longer than ${SHARD_TIMEOUT_MS / 1000}s — the worker likely stalled. Try again.`,
          ),
        );
      }, SHARD_TIMEOUT_MS);
      p.then(
        (v) => {
          clearTimeout(t);
          resolve(v);
        },
        (e) => {
          clearTimeout(t);
          reject(e);
        },
      );
    });

  let spendParams: Uint8Array;
  let outputParams: Uint8Array;
  try {
    // Sequential so the unified bar always moves forward monotonically.
    spendParams = await withTimeout(
      ensureCanonicalParams("saplingSpend", reportParams("saplingSpend")),
      "Loading spend params",
    );
    outputParams = await withTimeout(
      ensureCanonicalParams("saplingOutput", reportParams("saplingOutput")),
      "Loading output params",
    );
    onProgress?.({
      stage: "loading-params",
      params: { pct: 100, label: "Proving keys ready" },
    });
  } catch (e) {
    if (e instanceof SendShieldedError) throw e;
    throw new SendShieldedError(
      "PARAMS_FETCH_FAILED",
      e instanceof Error ? e.message : String(e),
      e,
    );
  }

  // 4. Compose the signer args and prove in a worker. Match the JSON
  //    shape leokit-shielded-sign expects (see lib.rs's BuildArgs).
  onProgress?.({ stage: "proving" });
  const signerNotes = picked.notes.map((n: SpendableNote) => ({
    recipient: n.recipient,
    amount_zats: n.value_zats,
    rseed: n.rseed,
    position: n.position,
    witness: n.witness,
  }));
  // target_height is the block this tx is constructed for — drives the
  // network-upgrade selection inside the builder and the default
  // expiry. lastSyncedHeight is the chain tip the scanner has confirmed
  // up to. We aim ~3 blocks ahead so the expiry window (target+20) has
  // breathing room for proving + broadcasting on slow devices. Do NOT
  // use anchorHeight here — the anchor is an older Merkle root the
  // witnesses agree on, not the current tip; aiming at anchor+1 would
  // target an already-mined block on most polls.
  const targetHeight = fresh.lastSyncedHeight + 3;
  // The picked notes ARE the spent notes — capture their nullifiers
  // before sending to the worker so we can prune locally even though
  // the Rust signer's BuildResultJson only returns { tx_hex, txid }.
  // Without this every send re-uses the same notes → double-spend on
  // the next attempt.
  const spentNullifiers = picked.notes.map((n: SpendableNote) => n.nullifier);
  const signArgs = {
    seed_phrase: args.seed_phrase,
    account: args.account ?? 0,
    notes: signerNotes,
    anchor_hex: fresh.anchor,
    target_height: targetHeight,
    // The signer accepts fee_zats but recomputes via Zip317FeeRule::standard()
    // internally — we send it for now to satisfy the BuildArgs deserializer.
    // If the signer is ever updated to honor this as authoritative, our
    // coin-select fee math already matches the rule.
    fee_zats: picked.fee_zats,
    outputs: [
      {
        recipient: args.recipient,
        amount_zats: args.amount_zats,
        memo: args.memo ?? null,
      },
    ],
    memo_note: args.memo_note ?? null,
  };

  let built: BuiltTx;
  try {
    built = await signInWorker({
      signArgsJson: JSON.stringify(signArgs),
      spendParams,
      outputParams,
    });
  } catch (e) {
    throw new SendShieldedError(
      "PROOF_FAILED",
      e instanceof Error ? e.message : String(e),
      e,
    );
  }

  // 5. Broadcast via the shielded backend (which is the lightwalletd
  //    forwarder — stateless, no UFVK seen here).
  onProgress?.({ stage: "broadcasting" });
  try {
    const client = getShieldedClient();
    await client.txBroadcast(built.tx_hex);
  } catch (e) {
    throw new SendShieldedError(
      "BROADCAST_FAILED",
      e instanceof Error ? e.message : String(e),
      e,
    );
  }

  // 6. Optimistically (but reversibly) mark the spent nullifiers as
  //    pending. The note value drops out of `netBalanceZats` until
  //    either the scanner confirms the spend on-chain OR a timeout
  //    elapses without confirmation — the latter means the relay
  //    accepted but didn't propagate (a real failure mode we've seen)
  //    and the funds rejoin the balance automatically.
  //    Use the locally-captured nullifiers because the Rust signer's
  //    BuildResultJson currently omits spent_nullifiers (see comment above).
  await markSpentLocally(
    args.ufvk,
    built.spent_nullifiers ?? spentNullifiers,
    built.txid,
  );

  onProgress?.({ stage: "done" });
  return {
    txid: built.txid,
    fee_zats: built.fee_zats ?? picked.fee_zats,
    spent_nullifiers: built.spent_nullifiers ?? [],
  };
}
